/**
 * The MultiPro's own safety rules (payload v2, up to 16 per board, `PAYLOAD-V2.md` "Safety
 * rules"): turning one off for a while, on, off or resetting it from the app, and reading the
 * 41-byte form well enough to say in a sentence what a rule does.
 *
 * The rules are set up with the Toolbox over USB. Turning one off from the app is for a rule
 * that is playing up, so nobody has to drive out: a timed off (V0.75+) is turned back on by the
 * board itself, even if the link is down by then. Every command is answered by `FF C4` and only
 * counts as done when that answer shows the change.
 */
import { crc16Modbus } from "./crc.ts";
import { portOfChannel, V2_FPORT } from "./v2.ts";
import type { PortRef, Uplink } from "./types.ts";

export const V2_RULES_MAX = 16;
export const V2_RULE_BYTES = 41;

/** A binary downlink for an FMS board, with how to recognise the board's answer. */
export interface V2Command {
  /** Hex of the bytes, for the command log. */
  text: string;
  description: string;
  bytes: Uint8Array;
  fPort: number;
  confirm: (u: Uplink) => boolean;
}

// ---------------------------------------------------------------- firmware

/** "FMSR3 V0.75 A" → 75 (hundredths: V1.02 → 102). undefined when it isn't an FMS version. */
export function fmsFirmwareNumber(firmware: string | undefined): number | undefined {
  const m = /V(\d+)\.(\d{1,2})/.exec(firmware ?? "");
  if (!m) return undefined;
  return Number(m[1]) * 100 + Number(m[2]!.padEnd(2, "0"));
}

/**
 * Turning rules on, off, off for a while and resetting from the app: V0.75 and later. Kane
 * asked for V0.74 and up; V0.74 takes FF BD but never answers it, so the app couldn't show the
 * change as done, and older boards are left alone.
 */
export const RULE_CONTROL_FROM = 75;

export function canControlRules(firmware: string | undefined): boolean {
  return (fmsFirmwareNumber(firmware) ?? 0) >= RULE_CONTROL_FROM;
}

// ---------------------------------------------------------------- commands

const hex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("").toUpperCase();

function command(bytes: number[], description: string, confirm: (u: Uplink) => boolean): V2Command {
  const b = Uint8Array.from(bytes);
  return { text: hex(b), description, bytes: b, fPort: V2_FPORT, confirm };
}

function checkRule(n: number): void {
  if (!Number.isInteger(n) || n < 1 || n > V2_RULES_MAX) throw new RangeError(`safety rule ${n} is not 1-${V2_RULES_MAX}`);
}

const statusOf = (n: number, test: (u: Extract<Uplink, { type: "safety-rule-status" }>) => boolean) => (u: Uplink) =>
  u.type === "safety-rule-status" && u.rule === n && test(u);

/** Turn rule n on (stored). Also ends a timed off early. Answered FF C4 with the rule on. */
export function ruleOn(n: number): V2Command {
  checkRule(n);
  return command([0xff, 0xbd, n, 1], `turn safety rule ${n} on`, statusOf(n, (u) => u.enabled || u.crc === 0));
}

/** Turn rule n off until someone turns it on again (stored: stays off after a restart). */
export function ruleOff(n: number): V2Command {
  checkRule(n);
  return command([0xff, 0xbd, n, 0], `turn safety rule ${n} off`, statusOf(n, (u) => !u.enabled));
}

/** Turn rule n off for a while; the board turns it back on itself (V0.75+). */
export function ruleOffFor(n: number, minutes: number): V2Command {
  checkRule(n);
  if (!Number.isInteger(minutes) || minutes < 1 || minutes > 0xffff) throw new RangeError("minutes must be 1-65535");
  return command(
    [0xff, 0xc3, n, minutes & 0xff, minutes >> 8],
    `turn safety rule ${n} off for ${durationText(minutes)}`,
    statusOf(n, (u) => !u.enabled && u.offMinutesLeft > 0),
  );
}

/** Reset rule n: un-latch, let go of outputs it locked off. */
export function ruleReset(n: number): V2Command {
  checkRule(n);
  return command([0xff, 0xbc, n], `reset safety rule ${n}`, statusOf(n, (u) => !u.latched));
}

/** Ask for the status of rule n, or every rule set on the board. */
export function ruleStatus(n: number | "all"): V2Command {
  if (n !== "all") checkRule(n);
  const k = n === "all" ? 0xff : n;
  return command([0xff, 0xc4, k], n === "all" ? "ask for every safety rule's status" : `ask for safety rule ${n}'s status`, (u) =>
    u.type === "safety-rule-status" && (n === "all" || u.rule === n),
  );
}

export function durationText(minutes: number): string {
  if (minutes % 1440 === 0) return `${minutes / 1440} day${minutes === 1440 ? "" : "s"}`;
  if (minutes % 60 === 0) return `${minutes / 60} hour${minutes === 60 ? "" : "s"}`;
  if (minutes > 60) return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
  return `${minutes} min`;
}

// ---------------------------------------------------------------- the 41 bytes

export type V2RuleCondition =
  | { type: "di"; port: PortRef; on: boolean; forS: number }
  | { type: "output"; port: PortRef; on: boolean; forS: number }
  | { type: "ai"; port: PortRef; op: RuleOp; raw: number; raw2?: number; forS: number }
  | { type: "flow"; port: PortRef; op: "below" | "above"; perMin: number; forS: number }
  | { type: "time"; days: number[]; from: number; to: number; forS: number } // days 0 Mon .. 6 Sun, minutes of day
  | { type: "link"; ok: boolean; forS: number }
  | { type: "temperature"; op: "below" | "above"; celsius: number; forS: number }
  | { type: "d2d"; command: number; forS: number }
  | { type: "rule"; rule: number; active: boolean; forS: number }
  // V0.77+: a value the board reads from its Modbus device, as the device sends it (no scaling).
  | { type: "modbus"; channel: number; op: RuleOp | "not-answering"; value: number; value2?: number; forS: number };

export type V2RuleAction =
  | { type: "output"; port: PortRef; state: "off" | "on" | "pulse"; delayS: number }
  | { type: "ao"; port: PortRef; raw: number; delayS: number }
  | { type: "d2d"; command: number; delayS: number }
  | { type: "rule"; rule: number; enable: boolean; delayS: number }
  | { type: "reset-count"; port: PortRef; delayS: number }
  | { type: "report"; delayS: number };

export type RuleOp = "below" | "above" | "between" | "outside";

export interface V2Rule {
  enabled: boolean;
  or: boolean;
  latch: boolean;
  lock: boolean;
  rearmS: number;
  conditions: V2RuleCondition[];
  actions: V2RuleAction[];
}

const OPS: RuleOp[] = ["below", "above", "between", "outside"];
const u16 = (b: Uint8Array, i: number) => b[i]! | (b[i + 1]! << 8);
const i16 = (b: Uint8Array, i: number) => {
  const v = u16(b, i);
  return v > 0x7fff ? v - 0x10000 : v;
};

function port(ch: number, kinds: PortRef["kind"][]): PortRef | null {
  const p = portOfChannel(ch);
  return p && kinds.includes(p.kind) ? p : null;
}

/** The board's 41-byte rule, as the firmware checks it (fms_rules.c); null if it would refuse it. */
export function decodeV2Rule(bytes: Uint8Array): V2Rule | null {
  if (bytes.length !== V2_RULE_BYTES) return null;
  const f = bytes[0]!;
  const rule: V2Rule = { enabled: (f & 1) !== 0, or: (f & 2) !== 0, latch: (f & 4) !== 0, lock: (f & 8) !== 0, rearmS: u16(bytes, 1), conditions: [], actions: [] };
  for (let i = 0; i < 2; i++) {
    const o = 3 + 10 * i;
    const t = bytes[o]!, ch = bytes[o + 1]!, op = bytes[o + 2]!, a = i16(bytes, o + 4), b = i16(bytes, o + 6), forS = u16(bytes, o + 8);
    let c: V2RuleCondition;
    switch (t) {
      case 0:
        if (i === 0) return null;
        continue;
      case 1: {
        const p = port(ch, ["di"]);
        if (!p || op > 1) return null;
        c = { type: "di", port: p, on: op === 1, forS };
        break;
      }
      case 2: {
        const p = port(ch, ["do", "ro"]);
        if (!p || op > 1) return null;
        c = { type: "output", port: p, on: op === 1, forS };
        break;
      }
      case 3: {
        const p = port(ch, ["ai"]);
        if (!p || op > 3 || a < 0 || a > 4095 || (op >= 2 && (b < a || b > 4095))) return null;
        c = op >= 2 ? { type: "ai", port: p, op: OPS[op]!, raw: a, raw2: b, forS } : { type: "ai", port: p, op: OPS[op]!, raw: a, forS };
        break;
      }
      case 4: {
        const p = port(ch, ["di"]);
        if (!p || op > 1 || a < 0) return null;
        c = { type: "flow", port: p, op: op === 1 ? "above" : "below", perMin: a, forS };
        break;
      }
      case 5: {
        if (!(ch & 0x7f) || a < 0 || a >= 1440 || b < 0 || b > 1440) return null;
        const days: number[] = [];
        for (let q = 0; q < 7; q++) if (ch & (1 << q)) days.push(q);
        c = { type: "time", days, from: a, to: b, forS };
        break;
      }
      case 6:
        if (op > 1) return null;
        c = { type: "link", ok: op === 1, forS };
        break;
      case 7:
        if (op > 1) return null;
        c = { type: "temperature", op: op === 1 ? "above" : "below", celsius: a / 10, forS };
        break;
      case 8:
        c = { type: "d2d", command: u16(bytes, o + 4), forS };
        break;
      case 9:
        if (ch < 1 || ch > 16 || op > 1) return null;
        c = { type: "rule", rule: ch, active: op === 1, forS };
        break;
      case 10:
        if (ch < 1 || ch > 16 || op > 4 || ((op === 2 || op === 3) && b < a)) return null;
        c = op === 4
          ? { type: "modbus", channel: ch, op: "not-answering", value: 0, forS }
          : op >= 2 ? { type: "modbus", channel: ch, op: OPS[op]!, value: a, value2: b, forS } : { type: "modbus", channel: ch, op: OPS[op]!, value: a, forS };
        break;
      default:
        return null;
    }
    rule.conditions.push(c);
  }
  for (let j = 0; j < 3; j++) {
    const p = 23 + 6 * j;
    const t = bytes[p]!, ch = bytes[p + 1]!, value = u16(bytes, p + 2), delayS = u16(bytes, p + 4);
    let x: V2RuleAction;
    switch (t) {
      case 0:
        continue;
      case 1: {
        const q = port(ch, ["do", "ro"]);
        if (!q || value > 2) return null;
        x = { type: "output", port: q, state: (["off", "on", "pulse"] as const)[value]!, delayS };
        break;
      }
      case 2: {
        const q = port(ch, ["ao"]);
        if (!q || value > 4095) return null;
        x = { type: "ao", port: q, raw: value, delayS };
        break;
      }
      case 3:
        x = { type: "d2d", command: value, delayS };
        break;
      case 4:
        if (ch < 1 || ch > 16 || value > 1) return null;
        x = { type: "rule", rule: ch, enable: value === 1, delayS };
        break;
      case 5: {
        const q = port(ch, ["di"]);
        if (!q) return null;
        x = { type: "reset-count", port: q, delayS };
        break;
      }
      case 6:
        x = { type: "report", delayS };
        break;
      default:
        return null;
    }
    rule.actions.push(x);
  }
  return rule;
}

/**
 * The CRC the board puts in `FF C4`: CRC-16/MODBUS of the 41 bytes with the "on" flag set,
 * so a rule turned off still matches the bytes it was set from.
 */
export function v2RuleCrc(bytes: Uint8Array): number {
  const b = Uint8Array.from(bytes);
  b[0] = b[0]! | 1;
  return crc16Modbus(b);
}

// ---------------------------------------------------------------- in words

/** Names ports the way the farm does ("Bore pump (RO1)"); the default is just the port. */
export type PortName = (p: PortRef) => string;
const plainName: PortName = (p) => `${p.kind.toUpperCase()}${p.channel}`;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const forText = (s: number) => (s ? ` for ${s >= 120 && s % 60 === 0 ? `${s / 60} min` : `${s} s`}` : "");

/**
 * "When Bore pump (RO1) is on and AI1 is below 768 for 20 s: Bore pump (RO1) off. Stays off
 * until reset." Analog values are the board's raw counts unless `analog` turns them into units.
 */
/** How to name a board Modbus channel and show its value (the app knows the port and its scale). */
export interface ModbusNamer {
  name(channel: number): string;
  value(channel: number, raw: number): string;
}

export function describeV2Rule(rule: V2Rule, name: PortName = plainName, analog?: (p: PortRef, raw: number) => string, modbus?: ModbusNamer): string {
  const val = (p: PortRef, raw: number) => (analog ? analog(p, raw) : `${raw}`);
  const mbName = (n: number) => (modbus ? modbus.name(n) : `Modbus channel ${n}`);
  const mbVal = (n: number, raw: number) => (modbus ? modbus.value(n, raw) : `${raw}`);
  const conds = rule.conditions.map((c) => {
    switch (c.type) {
      case "di": return `${name(c.port)} is ${c.on ? "on" : "off"}${forText(c.forS)}`;
      case "output": return `${name(c.port)} is ${c.on ? "on" : "off"}${forText(c.forS)}`;
      case "ai":
        return c.op === "between" || c.op === "outside"
          ? `${name(c.port)} is ${c.op} ${val(c.port, c.raw)} and ${val(c.port, c.raw2 ?? c.raw)}${forText(c.forS)}`
          : `${name(c.port)} is ${c.op} ${val(c.port, c.raw)}${forText(c.forS)}`;
      case "flow": return `flow on ${name(c.port)} is ${c.op} ${c.perMin} pulses/min${forText(c.forS)}`;
      case "time": return `it's ${c.days.length === 7 ? "any day" : c.days.map((d) => DAYS[d]).join(", ")} ${hhmm(c.from)}–${hhmm(c.to)}`;
      case "link": return `the link is ${c.ok ? "up" : "lost"}${forText(c.forS)}`;
      case "temperature": return `the board is ${c.op} ${c.celsius} °C${forText(c.forS)}`;
      case "d2d": return `D2D command ${c.command.toString(16).toUpperCase().padStart(4, "0")} comes in`;
      case "rule": return `rule ${c.rule} is ${c.active ? "active" : "not active"}${forText(c.forS)}`;
      case "modbus":
        if (c.op === "not-answering") return `${mbName(c.channel)} isn't answering${forText(c.forS)}`;
        return c.op === "between" || c.op === "outside"
          ? `${mbName(c.channel)} is ${c.op} ${mbVal(c.channel, c.value)} and ${mbVal(c.channel, c.value2 ?? c.value)}${forText(c.forS)}`
          : `${mbName(c.channel)} is ${c.op} ${mbVal(c.channel, c.value)}${forText(c.forS)}`;
    }
  });
  const acts = rule.actions.map((a) => {
    const after = a.delayS ? ` after ${a.delayS} s` : "";
    switch (a.type) {
      case "output": return `${name(a.port)} ${a.state}${after}`;
      case "ao": return `${name(a.port)} to ${a.raw}${after}`;
      case "d2d": return `send D2D ${a.command.toString(16).toUpperCase().padStart(4, "0")}${after}`;
      case "rule": return `rule ${a.rule} ${a.enable ? "on" : "off"}${after}`;
      case "reset-count": return `reset ${name(a.port)} count${after}`;
      case "report": return `send a report${after}`;
    }
  });
  let s = `When ${conds.join(rule.or ? " or " : " and ")}: ${acts.join(", ") || "nothing"}.`;
  if (rule.latch && rule.lock) s += " Stays tripped, and holds its outputs off, until reset.";
  else if (rule.latch) s += " Stays tripped until reset.";
  else if (rule.lock) s += " Holds its outputs off until reset.";
  return s;
}
